import React, { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import api from '../../services/api';
import { Search, Building2, Plus, Trash2, Upload } from 'lucide-react';
import toast from 'react-hot-toast';

const Institutions = () => {
  const [institutions, setInstitutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sectorFilter, setSectorFilter] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedInstitution, setSelectedInstitution] = useState(null);
  const [editData, setEditData] = useState({});
  const [districts, setDistricts] = useState([]);
  const [newInstitution, setNewInstitution] = useState({
    name: '', address: '', latitude: '', longitude: '',
    geo_fence_radius: 150, focal_person_name: '',
    focal_person_contact: '', training_sector: '',
    district_id: '', procurement_year: new Date().getFullYear(),
    contract_start_date: '', contract_end_date: '',
    courses: [{ course_name: '', number_of_trainees: '', start_date: '', end_date: '' }]
  });

  const fetchInstitutions = async () => {
    try {
      setLoading(true);
      const params = {};
      if (search) params.search = search;
      if (sectorFilter) params.sector = sectorFilter;
      const response = await api.get('/institutions', { params });
      const unique = response.data.institutions?.filter(
        (inst, index, self) => index === self.findIndex(i => i.id === inst.id)
      ) || [];
      setInstitutions(unique);
    } catch (err) {
      toast.error('Failed to load institutions');
    } finally {
      setLoading(false);
    }
  };

  const fetchDistricts = async () => {
    try {
      const response = await api.get('/districts');
      setDistricts(response.data.districts);
    } catch (err) {
      console.error('Failed to load districts');
    }
  };

  useEffect(() => {
    fetchInstitutions();
    fetchDistricts();
  }, []);

  useEffect(() => {
    fetchInstitutions();
  }, [search, sectorFilter]);

  const addCourse = () => {
    setNewInstitution({
      ...newInstitution,
      courses: [...newInstitution.courses,
        { course_name: '', number_of_trainees: '', start_date: '', end_date: '' }
      ]
    });
  };

  const removeCourse = (index) => {
    const updated = newInstitution.courses.filter((_, i) => i !== index);
    setNewInstitution({ ...newInstitution, courses: updated });
  };

  const updateCourse = (index, field, value) => {
    const updated = newInstitution.courses.map((course, i) =>
      i === index ? { ...course, [field]: value } : course
    );
    setNewInstitution({ ...newInstitution, courses: updated });
  };

  const handleCreateInstitution = async (e) => {
    e.preventDefault();
    try {
      await api.post('/institutions', newInstitution);
      toast.success('Institution created successfully!');
      setShowAddModal(false);
      setNewInstitution({
        name: '', address: '', latitude: '', longitude: '',
        geo_fence_radius: 150, focal_person_name: '',
        focal_person_contact: '', training_sector: '',
        district_id: '', procurement_year: new Date().getFullYear(),
        contract_start_date: '', contract_end_date: '',
        courses: [{ course_name: '', number_of_trainees: '', start_date: '', end_date: '' }]
      });
      fetchInstitutions();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create institution');
    }
  };

  const handleView = async (inst) => {
    setSelectedInstitution(inst);
    setShowViewModal(true);
    try {
      const response = await api.get(`/institutions/${inst.id}`);
      setSelectedInstitution({
        ...inst,
        courses: response.data.courses
      });
    } catch (err) {
      console.error('Failed to fetch institution details');
    }
  };

  const handleEdit = (inst) => {
    setSelectedInstitution(inst);
    setEditData({
      name: inst.name,
      address: inst.address || '',
      latitude: inst.latitude || '',
      longitude: inst.longitude || '',
      geo_fence_radius: inst.geo_fence_radius || 150,
      focal_person_name: inst.focal_person_name || '',
      focal_person_contact: inst.focal_person_contact || '',
      training_sector: inst.training_sector || '',
    });
    setShowEditModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this institution? This cannot be undone.')) return;
    try {
      await api.delete(`/institutions/${id}`);
      toast.success('Institution deleted successfully');
      fetchInstitutions();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete institution');
    }
  };

  const handleUpdateInstitution = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/institutions/${selectedInstitution.id}`, editData);
      toast.success('Institution updated successfully!');
      setShowEditModal(false);
      fetchInstitutions();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update institution');
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '—';
    return new Date(timestamp).toLocaleDateString('en-PK', {
      day: '2-digit', month: 'short', year: 'numeric'
    });
  };

  return (
    <Layout title="Institutions">

      {/* Top Bar */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-3 text-gray-400" />
            <input
              type="text"
              placeholder="Search institutions..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={sectorFilter}
            onChange={(e) => setSectorFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Sectors</option>
            <option value="Public">Public</option>
            <option value="Private">Private</option>
          </select>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => window.location.href = '/institutions/bulk-import'}
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition text-sm"
          >
            <Upload size={16} />
            Bulk Import
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 bg-blue-800 text-white px-4 py-2 rounded-lg hover:bg-blue-900 transition text-sm"
          >
            <Plus size={16} />
            Add Institution
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-blue-600">
          <p className="text-gray-500 text-sm">Total Institutions</p>
          <p className="text-3xl font-bold text-gray-800 mt-1">{institutions.length}</p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-green-600">
          <p className="text-gray-500 text-sm">Public Sector</p>
          <p className="text-3xl font-bold text-gray-800 mt-1">
            {institutions.filter(i => i.training_sector === 'Public').length}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow p-4 border-l-4 border-purple-600">
          <p className="text-gray-500 text-sm">Private Sector</p>
          <p className="text-3xl font-bold text-gray-800 mt-1">
            {institutions.filter(i => i.training_sector === 'Private').length}
          </p>
        </div>
      </div>

      {/* Institutions Table */}
      <div className="bg-white rounded-xl shadow">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">#</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Institution Name</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">District</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Sector</th>
                <th className="text-left px-4 py-3 text-gray-600 font-semibold">Focal Person</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">Courses</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">Trainees</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">Geo-fence</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">Status</th>
                <th className="text-center px-4 py-3 text-gray-600 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center py-8 text-gray-500">
                    Loading institutions...
                  </td>
                </tr>
              ) : institutions.length === 0 ? (
                <tr>
                  <td colSpan="10" className="text-center py-8 text-gray-500">
                    No institutions found
                  </td>
                </tr>
              ) : (
                institutions.map((inst, index) => (
                  <tr key={inst.id} className="border-t border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-500">{index + 1}</td>
                    <td className="px-4 py-3 font-medium text-gray-800">
                      <div>{inst.name}</div>
                      <div className="text-xs text-gray-400">{inst.address}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-600">{inst.district_name || '—'}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium
                        ${inst.training_sector === 'Public'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-purple-100 text-purple-700'}`}>
                        {inst.training_sector}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      <div>{inst.focal_person_name || '—'}</div>
                      <div className="text-xs text-gray-400">{inst.focal_person_contact}</div>
                    </td>
                    <td className="px-4 py-3 text-center text-gray-600">{inst.total_courses || 0}</td>
                    <td className="px-4 py-3 text-center text-gray-600">{inst.total_trainees || 0}</td>
                    <td className="px-4 py-3 text-center text-gray-600">{inst.geo_fence_radius}m</td>
                    <td className="px-4 py-3 text-center">
                      {inst.contract_active ? (
                        <span className="bg-green-100 text-green-700 px-2 py-1 rounded-full text-xs font-medium">Active</span>
                      ) : (
                        <span className="bg-gray-100 text-gray-500 px-2 py-1 rounded-full text-xs font-medium">No Contract</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button onClick={() => handleView(inst)}
                          className="text-blue-600 hover:text-blue-800 text-xs font-medium">View</button>
                        <span className="text-gray-300">|</span>
                        <button onClick={() => handleEdit(inst)}
                          className="text-green-600 hover:text-green-800 text-xs font-medium">Edit</button>
                        <span className="text-gray-300">|</span>
                        <button onClick={() => handleDelete(inst.id)}
                          className="text-red-500 hover:text-red-700 text-xs font-medium">Delete</button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
          Total: {institutions.length} institutions
        </div>
      </div>

      {/* Add Institution Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-screen overflow-y-auto">
            <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center">
              <h2 className="text-lg font-bold flex items-center gap-2">
                <Building2 size={20} />Add New Institution
              </h2>
              <button onClick={() => setShowAddModal(false)} className="text-white hover:text-blue-200 text-2xl font-bold">×</button>
            </div>
            <form onSubmit={handleCreateInstitution} className="p-6">
              <h3 className="font-bold text-gray-700 mb-3 pb-1 border-b">Basic Information</h3>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Institution Name *</label>
                  <input type="text" required value={newInstitution.name}
                    onChange={(e) => setNewInstitution({...newInstitution, name: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
                  <input type="text" value={newInstitution.address}
                    onChange={(e) => setNewInstitution({...newInstitution, address: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Sector *</label>
                  <select required value={newInstitution.training_sector}
                    onChange={(e) => setNewInstitution({...newInstitution, training_sector: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Select Sector</option>
                    <option value="Public">Public</option>
                    <option value="Private">Private</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Geo-fence Radius (meters)</label>
                  <input type="number" value={newInstitution.geo_fence_radius}
                    onChange={(e) => setNewInstitution({...newInstitution, geo_fence_radius: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Latitude *</label>
                  <input type="number" step="any" required value={newInstitution.latitude}
                    onChange={(e) => setNewInstitution({...newInstitution, latitude: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Longitude *</label>
                  <input type="number" step="any" required value={newInstitution.longitude}
                    onChange={(e) => setNewInstitution({...newInstitution, longitude: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Focal Person Name</label>
                  <input type="text" value={newInstitution.focal_person_name}
                    onChange={(e) => setNewInstitution({...newInstitution, focal_person_name: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Focal Person Contact</label>
                  <input type="text" value={newInstitution.focal_person_contact}
                    onChange={(e) => setNewInstitution({...newInstitution, focal_person_contact: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              <h3 className="font-bold text-gray-700 mb-3 pb-1 border-b">Contract Information</h3>
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">District</label>
                  <select value={newInstitution.district_id}
                    onChange={(e) => setNewInstitution({...newInstitution, district_id: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="">Select District</option>
                    {districts.map(d => (
                      <option key={d.id} value={d.id}>{d.district_name} — {d.division_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Procurement Year</label>
                  <input type="number" value={newInstitution.procurement_year}
                    onChange={(e) => setNewInstitution({...newInstitution, procurement_year: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Contract Start Date</label>
                  <input type="date" value={newInstitution.contract_start_date}
                    onChange={(e) => setNewInstitution({...newInstitution, contract_start_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Contract End Date</label>
                  <input type="date" value={newInstitution.contract_end_date}
                    onChange={(e) => setNewInstitution({...newInstitution, contract_end_date: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>

              <div className="flex justify-between items-center mb-3 pb-1 border-b">
                <h3 className="font-bold text-gray-700">Courses</h3>
                <button type="button" onClick={addCourse}
                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm font-medium">
                  <Plus size={14} /> Add Course
                </button>
              </div>

              {newInstitution.courses.map((course, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-3 mb-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-medium text-gray-600">Course {index + 1}</span>
                    {newInstitution.courses.length > 1 && (
                      <button type="button" onClick={() => removeCourse(index)} className="text-red-500 hover:text-red-700">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="col-span-2">
                      <label className="block text-xs text-gray-600 mb-1">Course Name</label>
                      <input type="text" placeholder="e.g. Electrician, Plumber"
                        value={course.course_name}
                        onChange={(e) => updateCourse(index, 'course_name', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">No. of Trainees</label>
                      <input type="number" placeholder="e.g. 30"
                        value={course.number_of_trainees}
                        onChange={(e) => updateCourse(index, 'number_of_trainees', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">Start Date</label>
                      <input type="date" value={course.start_date}
                        onChange={(e) => updateCourse(index, 'start_date', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                    </div>
                    <div>
                      <label className="block text-xs text-gray-600 mb-1">End Date</label>
                      <input type="date" value={course.end_date}
                        onChange={(e) => updateCourse(index, 'end_date', e.target.value)}
                        className="w-full border border-gray-300 rounded px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500" />
                    </div>
                  </div>
                </div>
              ))}

              <div className="flex gap-3 mt-4">
                <button type="button" onClick={() => setShowAddModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit"
                  className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900">Create Institution</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* View Modal */}
      {showViewModal && selectedInstitution && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-screen overflow-y-auto">
            <div className="bg-blue-900 text-white p-4 rounded-t-xl flex justify-between items-center">
              <h2 className="text-lg font-bold">Institution Details</h2>
              <button onClick={() => setShowViewModal(false)} className="text-white text-2xl font-bold">×</button>
            </div>
            <div className="p-6 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-gray-500 text-xs">Institution Name</p>
                  <p className="font-medium">{selectedInstitution.name}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Sector</p>
                  <p className="font-medium">{selectedInstitution.training_sector}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-gray-500 text-xs">Address</p>
                  <p className="font-medium">{selectedInstitution.address || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">District</p>
                  <p className="font-medium">{selectedInstitution.district_name || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Geo-fence Radius</p>
                  <p className="font-medium">{selectedInstitution.geo_fence_radius}m</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Latitude</p>
                  <p className="font-medium">{selectedInstitution.latitude}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Longitude</p>
                  <p className="font-medium">{selectedInstitution.longitude}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Focal Person</p>
                  <p className="font-medium">{selectedInstitution.focal_person_name || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Contact</p>
                  <p className="font-medium">{selectedInstitution.focal_person_contact || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Total Courses</p>
                  <p className="font-medium">{selectedInstitution.total_courses || 0}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Total Trainees</p>
                  <p className="font-medium">{selectedInstitution.total_trainees || 0}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Procurement Year</p>
                  <p className="font-medium">{selectedInstitution.procurement_year || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Contract Status</p>
                  <p className="font-medium">
                    {selectedInstitution.contract_active ? '✅ Active' : '❌ No Contract'}
                  </p>
                </div>
              </div>

              {/* Courses Table */}
              {selectedInstitution.courses?.length > 0 && (
                <div className="mt-4">
                  <p className="text-gray-500 text-xs font-medium mb-2 uppercase">Courses</p>
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50">
                          <th className="text-left px-3 py-2 text-gray-600">#</th>
                          <th className="text-left px-3 py-2 text-gray-600">Course Name</th>
                          <th className="text-center px-3 py-2 text-gray-600">Trainees</th>
                          <th className="text-center px-3 py-2 text-gray-600">Start Date</th>
                          <th className="text-center px-3 py-2 text-gray-600">End Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedInstitution.courses.map((course, index) => (
                          <tr key={course.id} className="border-t border-gray-100">
                            <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                            <td className="px-3 py-2 font-medium text-gray-800">{course.course_name}</td>
                            <td className="px-3 py-2 text-center text-gray-600">{course.number_of_trainees}</td>
                            <td className="px-3 py-2 text-center text-gray-600">
                              {formatDate(course.commencement_date)}
                            </td>
                            <td className="px-3 py-2 text-center text-gray-600">
                              {formatDate(course.expected_end_date)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex gap-3 mt-4">
                <button onClick={() => setShowViewModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Close</button>
                <button onClick={() => { setShowViewModal(false); handleEdit(selectedInstitution); }}
                  className="flex-1 bg-blue-800 text-white py-2 rounded-lg text-sm hover:bg-blue-900">Edit Institution</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedInstitution && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-screen overflow-y-auto">
            <div className="bg-green-700 text-white p-4 rounded-t-xl flex justify-between items-center">
              <h2 className="text-lg font-bold">Edit Institution</h2>
              <button onClick={() => setShowEditModal(false)} className="text-white text-2xl font-bold">×</button>
            </div>
            <form onSubmit={handleUpdateInstitution} className="p-6">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Institution Name</label>
                  <input type="text" required value={editData.name}
                    onChange={(e) => setEditData({...editData, name: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Address</label>
                  <input type="text" value={editData.address}
                    onChange={(e) => setEditData({...editData, address: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Sector</label>
                  <select value={editData.training_sector}
                    onChange={(e) => setEditData({...editData, training_sector: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="Public">Public</option>
                    <option value="Private">Private</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Geo-fence Radius (m)</label>
                  <input type="number" value={editData.geo_fence_radius}
                    onChange={(e) => setEditData({...editData, geo_fence_radius: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Latitude</label>
                  <input type="number" step="any" value={editData.latitude}
                    onChange={(e) => setEditData({...editData, latitude: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Longitude</label>
                  <input type="number" step="any" value={editData.longitude}
                    onChange={(e) => setEditData({...editData, longitude: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Focal Person Name</label>
                  <input type="text" value={editData.focal_person_name}
                    onChange={(e) => setEditData({...editData, focal_person_name: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Focal Person Contact</label>
                  <input type="text" value={editData.focal_person_contact}
                    onChange={(e) => setEditData({...editData, focal_person_contact: e.target.value})}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div className="flex gap-3 mt-4">
                <button type="button" onClick={() => setShowEditModal(false)}
                  className="flex-1 border border-gray-300 text-gray-700 py-2 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit"
                  className="flex-1 bg-green-700 text-white py-2 rounded-lg text-sm hover:bg-green-800">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

    </Layout>
  );
};

export default Institutions;